import { useEffect, useState } from 'react';
import { Button } from '@rocco/components/button';
import { Textarea } from '@rocco/components/textarea';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { cn } from '@rocco/utils/cn';
import { UserFeedbackRating } from 'generated/graphql';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { GATE_PANE_BRAND_DEFAULT } from '~/modules-rocco/user/auth-gate/gate-pane-image';

/** Copy for the form, kept together so the wording is changed in one place. */
export const SITE_FEEDBACK_COPY = {
    title: 'How are the new pages working for you?',
    statement: 'Tell us what you think of the new ArchiPro.',
    ratingLabel: 'HOW IS THIS PAGE?',
    messageLabel: 'MESSAGE',
    placeholder:
        'What were you doing, and what did you expect to happen instead?',
    sendLabel: 'Send feedback',
    retryLabel: 'Try again',
    failedMessage:
        'That did not save. Your note is still here — try again, and tell the team in Slack if it keeps failing.',
    thanksTitle: 'Thanks — that went straight to the team.',
    thumbUpLabel: 'This page works well',
    thumbDownLabel: 'Something is wrong with this page',
} as const;

export interface SiteFeedbackModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Persists the rating and the trimmed comment. Rejects on a failed write. */
    onSubmit: (input: {
        rating: UserFeedbackRating;
        comment: string;
    }) => Promise<void>;
    submitting: boolean;
}

/**
 * The sitewide feedback form: a thumbs rating and an optional note, on the same two-pane surface
 * Business Manager's feedback prompt uses.
 *
 * Business Manager asks its question unprompted, so it splits into a toast that catches a rating
 * and a modal that follows up for words. This one is opened on purpose by someone who already has
 * something to say, so both halves are on screen at once and neither is a separate step.
 */
export const SiteFeedbackModal = ({
    open,
    onOpenChange,
    onSubmit,
    submitting,
}: SiteFeedbackModalProps) => {
    const [rating, setRating] = useState<UserFeedbackRating | null>(null);
    const [comment, setComment] = useState('');
    const [sent, setSent] = useState(false);
    // The reason a send failed, or null when it has not. The audience is staff, so the raw cause
    // is shown: it is what they paste into Slack when they report it.
    const [failure, setFailure] = useState<string | null>(null);
    const failed = failure !== null;

    // Reopening is a fresh report about a different page, not a return to the last one.
    useEffect(() => {
        if (open) return;
        setRating(null);
        setComment('');
        setSent(false);
        setFailure(null);
    }, [open]);

    const canSend = rating !== null && !submitting;

    /**
     * Only claim success once the row is actually stored. A failed write keeps the form, the
     * rating and the words exactly as they were, because the alternative is telling someone their
     * feedback reached the team and dropping it.
     */
    const handleSend = async () => {
        if (!canSend) return;
        setFailure(null);
        try {
            await onSubmit({ rating, comment: comment.trim() });
            setSent(true);
        } catch (err) {
            setFailure(err instanceof Error ? err.message : String(err));
        }
    };

    return (
        <TwoPaneModal
            open={open}
            onOpenChange={onOpenChange}
            title={SITE_FEEDBACK_COPY.title}
            bodyPadding="roomy"
            corners="rounded"
            pane={
                <AuthModalBrandPane
                    image={GATE_PANE_BRAND_DEFAULT}
                    statement={SITE_FEEDBACK_COPY.statement}
                />
            }
        >
            <div className="flex h-full flex-col">
                <h2 className="max-w-88.5 text-8! leading-8.5! font-medium tracking-tight text-[#181818]">
                    {sent
                        ? SITE_FEEDBACK_COPY.thanksTitle
                        : SITE_FEEDBACK_COPY.title}
                </h2>

                {sent ? null : (
                    <>
                        <fieldset className="mt-8 flex flex-col gap-2">
                            <legend className="text-3 font-medium tracking-tight text-[#474747] uppercase">
                                {SITE_FEEDBACK_COPY.ratingLabel}
                            </legend>
                            <div className="mt-2 flex gap-3">
                                <ThumbButton
                                    label={SITE_FEEDBACK_COPY.thumbUpLabel}
                                    glyph="👍"
                                    selected={
                                        rating === UserFeedbackRating.ThumbUp
                                    }
                                    disabled={submitting}
                                    onClick={() =>
                                        setRating(UserFeedbackRating.ThumbUp)
                                    }
                                />
                                <ThumbButton
                                    label={SITE_FEEDBACK_COPY.thumbDownLabel}
                                    glyph="👎"
                                    selected={
                                        rating === UserFeedbackRating.ThumbDown
                                    }
                                    disabled={submitting}
                                    onClick={() =>
                                        setRating(UserFeedbackRating.ThumbDown)
                                    }
                                />
                            </div>
                        </fieldset>

                        <label className="mt-8 flex flex-1 flex-col gap-2">
                            <span className="text-3 font-medium tracking-tight text-[#474747] uppercase">
                                {SITE_FEEDBACK_COPY.messageLabel}
                            </span>
                            <Textarea
                                color="gray"
                                spacing="medium"
                                focusStyle="none"
                                value={comment}
                                onChange={(event) =>
                                    setComment(event.target.value)
                                }
                                placeholder={SITE_FEEDBACK_COPY.placeholder}
                                disabled={submitting}
                                className="min-h-48 grow resize-none rounded-2.5 text-4.5! placeholder:text-4.5 placeholder:text-gray-900/40"
                            />
                        </label>

                        {failed ? (
                            <p
                                role="alert"
                                className="mt-4 text-3.5 text-red-600"
                            >
                                {SITE_FEEDBACK_COPY.failedMessage}
                                <span className="mt-1 block text-3 break-words text-red-600/70">
                                    {failure}
                                </span>
                            </p>
                        ) : null}

                        <Button
                            type="button"
                            color="dark"
                            overflow="visible"
                            className="mt-8 h-14.25 w-full px-8 py-0 text-5!"
                            disabled={!canSend}
                            onClick={() => {
                                void handleSend();
                            }}
                        >
                            {failed
                                ? SITE_FEEDBACK_COPY.retryLabel
                                : SITE_FEEDBACK_COPY.sendLabel}
                        </Button>
                    </>
                )}
            </div>
        </TwoPaneModal>
    );
};

interface ThumbButtonProps {
    label: string;
    glyph: string;
    selected: boolean;
    disabled: boolean;
    onClick: () => void;
}

/** One half of the rating control. Labelled for screen readers; the glyph is decoration. */
const ThumbButton = ({
    label,
    glyph,
    selected,
    disabled,
    onClick,
}: ThumbButtonProps) => (
    <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        className={cn(
            'flex size-14 items-center justify-center rounded-2.5 border text-6 transition-colors',
            selected
                ? 'border-[#181818] bg-[#181818]'
                : 'border-gray-900/15 bg-white hover:border-gray-900/40',
            disabled && 'opacity-50',
        )}
    >
        <span aria-hidden="true">{glyph}</span>
    </button>
);
